<template>
  <div class="generate-list-page">
    <!-- 页面头部 -->
    <div class="generate-list-header">
      <div class="generate-list-header-text">
        <div class="generate-list-header-back">
          <img
            src="@/assets/icons/i-return.png"
            style="width: 36px; height: 36px; cursor: pointer"
            @click="goBack"
          />
          <div class="generate-list-header-title">全部最近文书</div>
        </div>
        <div class="generate-list-header-desc">
          查看全部要素式文书生成记录，可查看、下载已生成的文书。
        </div>
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="generate-list-toolbar">
      <div class="generate-list-search">
        <SearchOutlined class="search-icon" />
        <input
          v-model="keyword"
          type="text"
          placeholder="搜索文书名称"
          class="search-input"
        />
      </div>
    </div>

    <!-- 列表 -->
    <template v-if="loading || list.length">
      <div ref="listScrollRef" class="generate-history-list">
        <div v-if="loading && !list.length" class="generate-history-empty">
          加载中…
        </div>
        <article
          v-for="item in list"
          :key="item.id"
          class="generate-history-card"
          @click="viewRecord(item)"
        >
          <div class="generate-history-icon">
            <FileWordOutlined />
          </div>
          <div class="generate-history-main">
            <div class="generate-history-title" :title="item.name">
              <span class="generate-history-title-text">{{ item.name }}</span>
            </div>
            <div class="generate-history-meta">{{ item.meta }}</div>
          </div>
          <span
            class="history-status"
            :class="item.status === 'done' ? 'status-done' : 'status-running'"
          >
            <span v-if="item.status !== 'done'" class="mini-spinner"></span>
            {{ item.status === "done" ? "生成完成" : "生成中" }}
          </span>
          <div class="generate-history-time">{{ item.time }}</div>
          <div class="card-actions">
            <button class="view-btn" @click.stop="viewRecord(item)">
              查看
            </button>
            <button
              class="download-btn"
              :disabled="item.status !== 'done'"
              @click.stop="downloadRecord(item)"
            >
              下载
            </button>
            <button class="remove-btn" @click.stop="deleteRecord(item)">
              删除
            </button>
          </div>
        </article>
      </div>

      <!-- 分页器 -->
      <div v-if="total > 0" class="generate-history-pagination">
        <a-pagination
          v-model:current="currentPage"
          v-model:pageSize="pageSize"
          :total="total"
          :show-total="(t) => `共 ${t} 条`"
          show-size-changer
          :page-size-options="['10', '20', '50']"
          @change="onPageChange"
        />
      </div>
    </template>

    <!-- 空状态 -->
    <div v-else class="generate-history-empty">
      <a-empty />
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: "ElementBasedGenerateList" });
import { ref, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import { SearchOutlined, FileWordOutlined } from "@ant-design/icons-vue";
import {
  getIndictmentConversationPage,
  deleteIndictmentConversation,
  saveIndictmentDoc,
} from "@/api/elementBased";

const router = useRouter();

const keyword = ref("");
const list = ref([]);
const total = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const listScrollRef = ref(null);

const INPUT_TYPE_META = {
  natural: "要素式起诉状 · 文字描述生成",
  structured: "要素式起诉状 · 结构化生成",
  material: "要素式起诉状 · 案件材料生成",
  complaint: "传统起诉状转要素式",
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatRecordTime = (raw) => {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  const hm = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  const today = new Date();
  const startToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startTarget = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const diffDays = Math.round((startToday - startTarget) / 86400000);
  if (diffDays === 0) return `今天 ${hm}`;
  if (diffDays === 1) return `昨天 ${hm}`;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${hm}`;
};

// 实体同 Recent：无 status 字段，有下载地址即视为生成完成；文件名去扩展名展示
const normalizeRecord = (raw, index) => {
  const statusRaw = String(
    raw.status ?? raw.Status ?? raw.generateStatus ?? "",
  ).toLowerCase();
  const done =
    statusRaw === "completed" ||
    statusRaw === "done" ||
    statusRaw === "success" ||
    statusRaw === "2" ||
    (!statusRaw && !!raw.fileDownloadUrl);
  const inputType = String(
    raw.inputType ?? raw.InputType ?? raw.sourceType ?? "",
  ).toLowerCase();
  return {
    id: raw.id ?? raw.sessionId ?? raw.SessionId ?? `page-${index}`,
    aiChatsId: raw.aiChatsId ?? raw.aIChatsId ?? raw.AIChatsId ?? "",
    sessionId: raw.sessionId ?? raw.SessionId ?? raw.id,
    name:
      raw.name ??
      raw.title ??
      (raw.fileName
        ? raw.fileName.replace(/\.(docx?|pdf)$/i, "")
        : undefined) ??
      raw.caseReasonName ??
      raw.CaseReasonName ??
      "未命名文书",
    meta:
      raw.meta ??
      raw.description ??
      (raw.plaintiff && raw.defendant
        ? `${raw.plaintiff} 诉 ${raw.defendant}`
        : undefined) ??
      INPUT_TYPE_META[inputType] ??
      "要素式起诉状",
    status: done ? "done" : "generating",
    time: formatRecordTime(
      raw.time ?? raw.createTime ?? raw.CreateTime ?? raw.completeTime,
    ),
    fileDownloadUrl: raw.fileDownloadUrl ?? raw.FileDownloadUrl,
  };
};

const fetchList = async () => {
  loading.value = true;
  try {
    const res = await getIndictmentConversationPage({
      pageIndex: currentPage.value,
      pageSize: pageSize.value,
      keyword: keyword.value.trim() || undefined,
    });
    const data = res?.data ?? res ?? {};
    const rows =
      data.list ??
      data.items ??
      data.records ??
      data.data ??
      (Array.isArray(data) ? data : Array.isArray(res) ? res : []);
    list.value = rows.map(normalizeRecord);
    total.value =
      data.total ?? data.totalCount ?? data.count ?? rows.length;
  } catch (err) {
    console.error("[ElementBased] 获取文书分页失败:", err);
    list.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

onMounted(fetchList);

let searchTimer = null;
watch(keyword, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchList();
  }, 300);
});

const onPageChange = () => {
  fetchList();
  listScrollRef.value?.scrollTo({ top: 0, behavior: "smooth" });
};

// ---------- 动作 ----------
// 查看记录：进入聊天记录页（可续聊），
// id 为记录 id（Detail / 下载用），chatId 为 aiChatsId（查历史 / 续聊用）
const viewRecord = (item) => {
  router.push({
    path: "/home/ElementBased/generateResults",
    query: {
      view: "chat",
      id: item?.id ?? "",
      chatId: item?.aiChatsId || "",
      name: item?.name || "",
    },
  });
};
const downloadRecord = async (item) => {
  if (!item?.id || item.status !== "done") return;
  try {
    await saveIndictmentDoc(item);
    message.success("文件下载成功");
  } catch (err) {
    console.error("[ElementBased] 下载文书失败:", err);
    message.error("文书下载失败，请稍后重试");
  }
};

const deletingId = ref(null);
const deleteRecord = (item) => {
  if (!item?.id || deletingId.value != null) return;
  Modal.confirm({
    title: "删除文书记录",
    content: `确定删除「${item.name || "未命名文书"}」吗？删除后不可恢复。`,
    okText: "确认",
    cancelText: "取消",
    onOk: async () => {
      deletingId.value = item.id;
      try {
        await deleteIndictmentConversation(item.id);
        message.success("已删除");
        if (list.value.length === 1 && currentPage.value > 1) {
          currentPage.value -= 1;
        }
        await fetchList();
      } catch (err) {
        console.error("[ElementBased] 删除文书失败:", err);
        message.error("删除失败");
      } finally {
        deletingId.value = null;
      }
    },
  });
};

const goBack = () => {
  router.push("/home/ElementBased");
};
</script>

<style>
/* 文书列表页显示时，禁止外层 .content-wrapper 自身滚动；
   页面用绝对定位占满 .content（已 position:relative），仅列表内部滚动 */
.content-wrapper:has(.generate-list-page) {
  overflow: hidden;
}
</style>

<style lang="less" scoped>
.generate-list-page {
  /* 用绝对定位占满 .content（已 position:relative），
     不依赖父级 flex 高度链，整页不滚动，仅列表区域内部滚动 */
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  line-height: 1.5;
}

/* 头部 */
.generate-list-header {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}
.generate-list-header-back {
  display: flex;
  align-items: center;
  gap: 12px;
}
.generate-list-header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: #17243a;
}
.generate-list-header-desc {
  margin-top: 10px;
  color: #65758e;
  font-size: 15px;
  line-height: 1.7;
}

/* 搜索栏 */
.generate-list-toolbar {
  flex-shrink: 0;
  margin-top: 20px;
  min-height: 72px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 16px;
  box-shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
}
.generate-list-search {
  position: relative;
  width: min(360px, 100%);
  flex: none;
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #7d8ba1;
    font-size: 18px;
    pointer-events: none;
  }
  .search-input {
    width: 100%;
    height: 40px;
    padding: 0 13px 0 38px;
    border: 1px solid #d9e3f0;
    border-radius: 10px;
    background: #fff;
    color: #283b57;
    outline: none;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
    &::placeholder {
      color: #9aa6b7;
    }
    &:focus {
      border-color: #8fb6fb;
      box-shadow: 0 0 0 3px rgba(23, 104, 255, 0.07);
    }
  }
}

/* 列表（内部滚动） */
.generate-history-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  gap: 12px;
  margin-top: 16px;
  align-content: start;
  padding-right: 4px;
}
.generate-history-list::-webkit-scrollbar {
  width: 6px;
}
.generate-history-list::-webkit-scrollbar-thumb {
  background: rgba(23, 104, 255, 0.18);
  border-radius: 3px;
}
.generate-history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(23, 104, 255, 0.32);
}
.generate-history-list::-webkit-scrollbar-track {
  background: transparent;
}
.generate-history-card {
  min-height: 84px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 108px 150px 218px;
  align-items: center;
  column-gap: 14px;
  border: 1px solid #e1e8f2;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(31, 57, 105, 0.025);
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
  cursor: pointer;
  &:hover {
    border-color: #c8d8ef;
    box-shadow: 0 8px 22px rgba(31, 57, 105, 0.06);
    transform: translateY(-1px);
  }
}
.generate-history-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #edf4ff;
  color: #1768ff;
  display: grid;
  place-items: center;
  .anticon {
    font-size: 20px;
  }
}
.generate-history-main {
  min-width: 0;
}
.generate-history-title {
  font-size: 15px;
  font-weight: 780;
  color: #243752;
  display: flex;
  align-items: center;
  gap: 8px;
  .generate-history-title-text {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.generate-history-meta {
  margin-top: 7px;
  color: #718097;
  font-size: 12px;
  line-height: 1.55;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.history-status {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: max-content;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  &.status-done {
    background: #eaf8f2;
    color: #16825e;
  }
  &.status-running {
    background: #edf4ff;
    color: #1768ff;
  }
}
.mini-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid #cfe0ff;
  border-top-color: #1768ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.generate-history-time {
  color: #6f7e94;
  font-size: 12px;
  white-space: nowrap;
}
/* 操作按钮组（查看 / 下载 / 删除） */
.card-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.view-btn,
.download-btn,
.remove-btn {
  height: 32px;
  min-width: 56px;
  padding: 0 12px;
  border: 1px solid #dce4f0;
  border-radius: 8px;
  background: #fff;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s;
}
.view-btn,
.download-btn {
  color: #31435f;
  &:hover:not(:disabled) {
    background: #f8fbff;
    border-color: #c9d6e8;
  }
}
.download-btn:disabled {
  color: #a1adbd;
  background: #f4f6f9;
  border-color: #e1e6ee;
  cursor: not-allowed;
}
/* 类名不用 delete-btn：全局 enterprise-surface.css 有
   .content-wrapper .delete-btn { color: ... !important }（AI 聊天界面的覆盖），
   会把本页按钮文字压成灰色 */
.remove-btn {
  color: #e34d59;
  border-color: #ffccc7; /* antd red-2 */
  &:hover {
    background: #fff1f0; /* antd red-1 */
    border-color: #ffa39e; /* antd red-3 */
  }
}

/* 空状态（撑满剩余空间） */
.generate-history-empty {
  flex: 1;
  min-height: 0;
  margin-top: 16px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #7c899d;
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 16px;
  box-shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
}

/* 分页器（固定在页面底部） */
.generate-history-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  padding-bottom: 4px;
}

@media (max-width: 1320px) {
  .generate-history-card {
    grid-template-columns: 42px minmax(0, 1fr) 104px 142px 210px;
    column-gap: 10px;
  }
}
</style>
